import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, FooterButton } from 'Core/components';
import CustomPropTypes from 'Core/prop-types';
import { AppModal, ConfirmModal } from '../../components/app-modals';
import { showToast } from 'Core/redux/toast/slice';
import {
  updateProcess,
  savePlan,
  openProcessingParameters,
  fetchWorkTicketStatusLog,
  deleteByWorkTicketId,
  fetchPlanner,
  fetchProcessCompletionStatus,
  actions as plannerActions,
} from '../store/slice';
import { selectChannels, selectStatusLog, selectStatusLogLoading, selectPlanFilters } from '../store/selectors';
import { selectors as locationSelectors } from 'Core/redux/location';
// Import status options from constants to ensure consistency
import { STATUS_COLORS, getStatusColor } from '../constants/statusColors';
import { fetchAllProcessStatuses } from 'Core/redux/status/slice';
import { selectProcessStatuses, selectProcessStatusLoading } from 'Core/redux/status/selectors';

const partFieldNames = [
  'partNumber',
  'boxQuantity',
  'palletQuantity',
  'plannedQuantity',
  'locationId',
  'licensePlateNumber',
  'po',
];

const priorityOptions = Array.from({ length: 10 }, (_, i) => i + 1);

const isNilOrEmpty = v => v == null || (typeof v === 'string' && v.trim() === '');

/**
 * getByWorkTicketId returns `cellNumber` (e.g. "C01"); the Machine control uses `channel.uuid`.
 * Map cell → matching channel so the dropdown shows the correct machine.
 */
function applyCellNumberToMachineFields(data, channelList) {
  const cellRaw = data.cellNumber ?? data.CellNumber;
  if (cellRaw == null || String(cellRaw).trim() === '') {
    return data;
  }
  const cellStr = String(cellRaw).trim();
  const cellNorm = cellStr.replace(/^C/i, '').trim();
  const list = Array.isArray(channelList) ? channelList : [];

  const match =
    list.find(ch => String(ch?.uuid ?? '').trim() === cellStr) ||
    list.find(ch => String(ch?.title ?? '').trim() === cellStr) ||
    list.find(ch => String(ch?.assetNumber ?? '').trim() === cellStr) ||
    list.find(ch => String(ch?.uuid ?? '').trim() === cellNorm) ||
    list.find(ch => String(ch?.title ?? '').trim() === cellNorm) ||
    list.find(ch => String(ch?.assetNumber ?? '').trim() === cellNorm);

  if (match != null && (match.uuid != null || match.id != null)) {
    const id = String(match.uuid ?? match.id);
    return { ...data, machineId: id, channelUuid: id };
  }

  return { ...data, machineId: cellStr, channelUuid: cellStr };
}

// Helper to safely convert various date representations into YYYY-MM-DD for <input type="date" />
const toInputDate = value => {
  if (!value) return '';

  // Strings like "2026-03-04" or "2026-03-04T00:00:00"
  if (typeof value === 'string') {
    const datePart = value.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
    // API often returns M/D/YYYY or MM/DD/YYYY (e.g. "04/22/2026")
    const mdy = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdy) {
      const month = mdy[1].padStart(2, '0');
      const day = mdy[2].padStart(2, '0');
      const year = mdy[3];
      return `${year}-${month}-${day}`;
    }
  }

  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

// Local time ISO-like string (no timezone "Z") to avoid day shifts.
const nowLocalIsoLike = () => {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
};

/** Normalize API datetime (ISO string or Date) to `yyyy-MM-ddTHH:mm:ss` for split date/time inputs. */
const toScheduleIsoString = v => {
  if (v == null) return null;
  if (typeof v === 'string') {
    const t = v.trim().replace(' ', 'T');
    return t === '' ? null : t;
  }
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const pad = n => String(n).padStart(2, '0');
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}T${pad(v.getHours())}:${pad(v.getMinutes())}:${pad(v.getSeconds())}`;
  }
  return null;
};

/** Task-board drop often supplies midnight; merge local wall-clock onto the date part for saveplan/API. */
const mergeCalendarIsoWithLocalNow = isoLike => {
  const s = String(isoLike || '')
    .replace(' ', 'T')
    .trim();
  const dp = s.split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dp)) return s;
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${dp}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

/** Parse yyyy-MM-dd (or ISO-like) to { year, month, day } in local calendar semantics. */
const parseScheduleDateParts = isoLike => {
  if (isNilOrEmpty(isoLike)) return null;
  const datePart = String(isoLike).trim().split('T')[0];
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    return {
      year: parseInt(m[1], 10),
      month: parseInt(m[2], 10),
      day: parseInt(m[3], 10),
    };
  }
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
};

/**
 * Continuous Run → concrete API end (Dec 31 @ 23:59:59).
 * If the **schedule end** (toDate/till before CR) is in **December**, use 31 Dec of the **next** calendar year.
 * Otherwise use 31 Dec of the **From** date's calendar year.
 */
const getContinuousRunEndIso = (fromValue, referenceEndValue) => {
  const ref = parseScheduleDateParts(referenceEndValue);
  if (ref && ref.month === 12) {
    return `${ref.year + 1}-12-31T23:59:59`;
  }
  const from = parseScheduleDateParts(fromValue);
  if (!from) return null;
  return `${from.year}-12-31T23:59:59`;
};

const getProcessIdentityKey = data => {
  if (!data) return null;
  return String(
    data.id ??
      data.processId ??
      data.workTicketNumber ??
      data.workTicketNo ??
      data.workTicket ??
      data.title ??
      '',
  );
};

const resolveWeekendOnOff = data => {
  if (!data || typeof data !== 'object') return false;
  if (data.weekendOnOff !== undefined) return !!data.weekendOnOff;
  if (data.WeekendOnOff !== undefined) return !!data.WeekendOnOff;
  if (data.weekend_on_off !== undefined) return !!data.weekend_on_off;
  if (data.isWeekendOn !== undefined) return !!data.isWeekendOn;
  return false;
};

const ProgramAddEdit = ({
  showModal: isShowModal,
  modalData: initialProcessData,
  onCloseModal,
}) => {
  const [processData, setProcessData] = useState(initialProcessData);
  const [activeTab, setActiveTab] = useState('details'); // details | log
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  /** After user picks another machine, show channel titles again (do not reuse API cell text on wrong row). */
  const machineCellLabelSuppressedRef = useRef(false);
  /** Schedule end (toDate/till) before CR transform — used to detect December → next year. */
  const continuousRunReferenceEndRef = useRef(null);
  /** Avoid wiping local edits when channels/list refresh re-triggers the init effect. */
  const initializedProcessKeyRef = useRef(null);

  const locations = useSelector(locationSelectors.selectLocations);
  const channels = useSelector(selectChannels);
  const statusLog = useSelector(selectStatusLog);
  const statusLogLoading = useSelector(selectStatusLogLoading);
  const processStatuses = useSelector(selectProcessStatuses);
  const processStatusLoading = useSelector(selectProcessStatusLoading);
  const planFilters = useSelector(selectPlanFilters);
  const dispatch = useDispatch();

  /** Same as processData.continuousRun — one render; disables End date/time while stretched to year-end. */
  const continuousRun = !!processData?.continuousRun;
  /** Default off when API omits the field. */
  const weekendOnOff = resolveWeekendOnOff(processData);

  // Fetch process statuses from API when component mounts
  useEffect(() => {
    dispatch(fetchAllProcessStatuses());
  }, [dispatch]);

  // Extract status options from API response
  // API response should have a 'status' field in each item
  const statusOptions = React.useMemo(() => {
    if (!processStatuses || !Array.isArray(processStatuses)) {
      // Fallback to hardcoded values if API fails
      return Object.keys(STATUS_COLORS).filter(key => !['All', 'Default'].includes(key));
    }
    // Extract unique status values from API response
    const statuses = processStatuses
      .map(item => item.status || item.Status || item.name)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index); // Get unique values
    return statuses.length > 0 ? statuses : Object.keys(STATUS_COLORS).filter(key => !['All', 'Default'].includes(key));
  }, [processStatuses]);

  useEffect(() => {
    if (!initialProcessData) return;

    const processKey = getProcessIdentityKey(initialProcessData);
    const isSameProcess =
      processKey != null &&
      processKey !== '' &&
      initializedProcessKeyRef.current === processKey;

    // Channels/list refresh often creates a new array reference. Don't wipe local toggle edits.
    if (isSameProcess) {
      if (!machineCellLabelSuppressedRef.current) {
        setProcessData(prev => applyCellNumberToMachineFields(prev || initialProcessData, channels));
      }
      return;
    }

    // Normalize From Date and schedule end (toDate) when modal opens; Due Date is not auto-mapped
    const nextData = { ...initialProcessData };

    // Prefer SQL datetime columns (full clock) for schedule; legacy nvarchar/date fields are fallbacks.
    const fromDateTimeApi =
      toScheduleIsoString(nextData.fromDate_Time) || toScheduleIsoString(nextData.FromDate_Time);
    const toDateTimeApi =
      toScheduleIsoString(nextData.toDate_Time) || toScheduleIsoString(nextData.ToDate_Time);

    // From Date + Start Time: prefer fromDate_Time from getById
    const resolvedFromDate =
      fromDateTimeApi ||
      nextData.fromDate ||
      nextData.FromDate ||
      nextData.from_date ||
      nextData.fromdate ||
      nextData.from ||
      nextData.since ||
      nextData.StartDate ||
      nextData.startDate;

    // End Date + End Time: prefer toDate_Time from getById
    const resolvedToDate =
      toDateTimeApi ||
      nextData.toDate ||
      nextData.ToDate ||
      nextData.to_date ||
      nextData.todate ||
      nextData.to ||
      nextData.till ||
      nextData.EndDate ||
      nextData.endDate;

    nextData.fromDate = resolvedFromDate || nextData.fromDate;
    const apiScheduleEnd =
      resolvedToDate ||
      nextData.toDate ||
      nextData.ToDate ||
      nextData.till ||
      nextData.Till ||
      null;
    nextData.toDate = apiScheduleEnd || nextData.toDate;

    if (fromDateTimeApi) {
      nextData.since = fromDateTimeApi;
    }
    if (toDateTimeApi) {
      nextData.till = toDateTimeApi;
    }

    const fromTaskBoardDrop = Boolean(initialProcessData?.__fromTaskBoardDrop);
    if (fromTaskBoardDrop) {
      if (nextData.fromDate && typeof nextData.fromDate === 'string') {
        const fd = nextData.fromDate.replace(' ', 'T').trim();
        if (/T00:00:00(\.\d+)?$/i.test(fd) || !fd.includes('T')) {
          nextData.fromDate = mergeCalendarIsoWithLocalNow(fd);
          nextData.since = nextData.fromDate;
        }
      }
      if (nextData.toDate && typeof nextData.toDate === 'string' && /T24:/i.test(nextData.toDate)) {
        const dp = String(nextData.fromDate || nextData.since || nextData.toDate).split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(dp)) {
          nextData.toDate = `${dp}T23:59:59`;
          nextData.till = nextData.toDate;
        }
      }
    }

    continuousRunReferenceEndRef.current = null;
    // Continuous run: API needs an end — Dec of next year if schedule end is in December, else Dec of From year.
    if (nextData.continuousRun && nextData.fromDate) {
      continuousRunReferenceEndRef.current = apiScheduleEnd;
      const eoy = getContinuousRunEndIso(nextData.fromDate, apiScheduleEnd);
      if (eoy) nextData.toDate = eoy;
    }

    // Normalize weekend flag once on open (API may omit or use alternate casing)
    nextData.weekendOnOff = resolveWeekendOnOff(nextData);

    // Prefer API `cellNumber` for Machine when it maps to a channel (dropdown values are `channel.uuid`).
    const withMachine = applyCellNumberToMachineFields(nextData, channels);

    initializedProcessKeyRef.current = processKey;
    setProcessData(withMachine);
    setActiveTab('details'); // reset tab when modal opens with new data
    setIsFormDirty(false); // reset form dirty state when modal opens with new data
    setIsDeleting(false); // reset delete loading state when modal opens with new data
    machineCellLabelSuppressedRef.current = false;
    // Note: statusLog is managed in Redux and will be fetched when Log tab is opened
  }, [initialProcessData, channels]);

  useEffect(() => {
    if (!isShowModal) {
      initializedProcessKeyRef.current = null;
    }
  }, [isShowModal]);

  // Fetch status log when Log tab is opened
  useEffect(() => {
    if (activeTab === 'log' && processData) {
      // Use workTicketNumber as the API expects workTicketNumber parameter
      const workTicketNumber =
        processData.workTicketNumber || processData.workTicketNo || processData.workTicket;
      if (workTicketNumber) {
        console.log('Fetching status log for work ticket:', workTicketNumber);
        dispatch(fetchWorkTicketStatusLog(workTicketNumber));
      }
    }
  }, [activeTab, processData?.workTicketNumber, dispatch]);

  // Debug: Log statusLog changes
  useEffect(() => {
    if (activeTab === 'log') {
      console.log('Status Log State:', {
        statusLog,
        statusLogLoading,
        statusLogLength: statusLog?.length,
      });
    }
  }, [activeTab, statusLog, statusLogLoading]);

  // Ensure modal fields get populated from API fields (since/till)
  // (reverted) keep original initialization only from initialProcessData

  const handleClose = () => {
    onCloseModal();
  };

  const handleToggleChange = (field, value) => {
    setIsFormDirty(true);
    if (field === 'weekendOnOff') {
      setProcessData(prevData => ({ ...prevData, weekendOnOff: value }));
    } else if (field === 'continuousRun') {
      if (value) {
        setProcessData(prevData => {
          const refEnd =
            prevData.toDate || prevData.ToDate || prevData.till || prevData.Till || null;
          continuousRunReferenceEndRef.current = refEnd;
          const from = prevData.fromDate || nowLocalIsoLike();
          const eoy = getContinuousRunEndIso(from, refEnd);
          const next = { ...prevData, continuousRun: true };
          if (eoy) next.toDate = eoy;
          return next;
        });
      } else {
        const prevEnd = continuousRunReferenceEndRef.current;
        continuousRunReferenceEndRef.current = null;
        setProcessData(prevData => ({
          ...prevData,
          continuousRun: false,
          ...(prevEnd
            ? {
                toDate: prevEnd,
                till: prevEnd,
                Till: prevEnd,
              }
            : {}),
        }));
      }
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setIsFormDirty(true);
    if (partFieldNames.some(fieldName => name.startsWith(fieldName))) {
      const index = parseInt(name.split('-')[1], 10);
      const fieldName = name.split('-')[0];
      const updatedParts = processData.processParts.map((part, i) =>
        i === index ? { ...part, [fieldName]: value } : part,
      );
      setProcessData(prevData => ({ ...prevData, processParts: updatedParts }));
    } else {
      // When machineId changes, also update channelUuid for consistency
      if (name === 'machineId') {
        machineCellLabelSuppressedRef.current = true;
        setProcessData(prevData => ({
          ...prevData,
          machineId: value,
          channelUuid: value, // Sync channelUuid with machineId
        }));
      } else if (name === 'fromDate' || name === 'startTime') {
        // Handle date and time combination for fromDate/startTime WITHOUT timezone shifts
        setProcessData(prevData => {
          const currentFromDate = prevData.fromDate || nowLocalIsoLike();
          const parts = currentFromDate.split('T');
          let datePart = parts[0] || '';
          let timePart = (parts[1] || '00:00:00').slice(0, 8);

          if (name === 'fromDate') {
            // Date changed - keep existing time
            datePart = value;
          } else {
            // Time changed - keep existing date
            timePart = value.length === 5 ? `${value}:00` : value;
          }

          const fromDate = `${datePart}T${timePart}`;
          const next = { ...prevData, fromDate };
          if (prevData.continuousRun) {
            const refEnd = continuousRunReferenceEndRef.current;
            const eoy = getContinuousRunEndIso(fromDate, refEnd);
            if (eoy) next.toDate = eoy;
          }
          return next;
        });
      } else if (name === 'endDate' || name === 'endTime') {
        // End Date / End Time update schedule toDate only (Due Date is separate / not mapped for now)
        setProcessData(prevData => {
          const currentToDate = prevData.toDate || nowLocalIsoLike();
          const parts = currentToDate.split('T');
          let datePart = parts[0] || '';
          let timePart = (parts[1] || '00:00:00').slice(0, 8);

          if (name === 'endDate') {
            // Date changed - keep existing time
            datePart = value;
          } else {
            // Time changed - keep existing date
            timePart = value.length === 5 ? `${value}:00` : value;
          }

          return { ...prevData, toDate: `${datePart}T${timePart}` };
        });
      } else {
        setProcessData(prevData => ({ ...prevData, [name]: value }));
      }
    }
  };

  const body = () => {
    return (
      <>
        <div className="d-flex gap-2 mb-3">
          <button
            type="button"
            className={`btn ${activeTab === 'details' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('details')}
          >
            Work Ticket Details
          </button>
          <button
            type="button"
            className={`btn ${activeTab === 'log' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('log')}
          >
            Log
          </button>
        </div>

        <div className="" style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
          <div>
            <div>
              {/* Toggle Buttons */}
              <div className="row mb-3 justify-content-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-center">
                    <label className="fw-bold me-3 mb-0" style={{ color: '#0d6efd' }} htmlFor="weekendOnOff">
                      Weekend
                    </label>
                    <label className="planner-on-off-toggle" htmlFor="weekendOnOff">
                      <input
                        type="checkbox"
                        id="weekendOnOff"
                        checked={weekendOnOff}
                        onChange={e => handleToggleChange('weekendOnOff', e.target.checked)}
                      />
                      <span className="planner-on-off-toggle__track" aria-hidden="true">
                        <span className="planner-on-off-toggle__text planner-on-off-toggle__text--on">
                          On
                        </span>
                        <span className="planner-on-off-toggle__text planner-on-off-toggle__text--off">
                          Off
                        </span>
                        <span className="planner-on-off-toggle__knob" />
                      </span>
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-center">
                    <label className="fw-bold me-3 mb-0" style={{ color: '#0d6efd' }} htmlFor="continuousRun">
                      Continuous Run
                    </label>
                    <label className="planner-on-off-toggle" htmlFor="continuousRun">
                      <input
                        type="checkbox"
                        id="continuousRun"
                        checked={continuousRun}
                        onChange={e => handleToggleChange('continuousRun', e.target.checked)}
                      />
                      <span className="planner-on-off-toggle__track" aria-hidden="true">
                        <span className="planner-on-off-toggle__text planner-on-off-toggle__text--on">
                          On
                        </span>
                        <span className="planner-on-off-toggle__text planner-on-off-toggle__text--off">
                          Off
                        </span>
                        <span className="planner-on-off-toggle__knob" />
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Machine</label>
                    <select
                      className="form-control"
                      name="machineId"
                      value={processData.machineId || ''}
                      onChange={handleChange}
                    >
                      {channels.map(channel => {
                        const cellLabel = processData.cellNumber ?? processData.CellNumber;
                        const selected = String(processData.machineId || '') === String(channel.uuid || '');
                        const useCell =
                          selected &&
                          !machineCellLabelSuppressedRef.current &&
                          cellLabel != null &&
                          String(cellLabel).trim() !== '';
                        const label = useCell
                          ? String(cellLabel).trim()
                          : channel.title || channel.assetNumber || channel.uuid;
                        return (
                          <option key={channel.uuid} value={channel.uuid}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  {/* <div className="mb-1">
                   <label className="fw-bold">Status</label>
                   <div className="d-flex align-items-center">
                     <div 
                       className="me-2 rounded" 
                       style={{ 
                         width: '20px', 
                         height: '20px', 
                         backgroundColor: STATUS_COLORS[processData.status] || STATUS_COLORS.Default,
                         border: '1px solid #ccc'
                       }}
                       title={`Status color: ${STATUS_COLORS[processData.status] || STATUS_COLORS.Default}`}
                     ></div>
                     <select
                       className="form-control"
                       name="status"
                       value={processData.status || ''}
                       onChange={handleChange}
                       disabled={processStatusLoading}
                     >
                       {processStatusLoading ? (
                         <option>Loading statuses...</option>
                       ) : (
                         statusOptions.map((status, index) => (
                           <option key={index} value={status}>
                             {status}
                           </option>
                         ))
                       )}
                     </select>
                   </div>
               </div> */}
                  <div className="mb-1">
                    <label className="fw-bold">Due Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="dueDate"
                      value={toInputDate(processData.dueDate || processData.DueDate || '')}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="fromDate"
                      value={toInputDate(
                        processData.fromDate ||
                          processData.startDate ||
                          processData.StartDate ||
                          processData.since,
                      )}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-1">
                    <label className="fw-bold">Start Time</label>
                    <input
                      type="time"
                      className="form-control"
                      name="startTime"
                      value={processData.fromDate ? (processData.fromDate.split('T')[1] || '').slice(0, 5) : ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="endDate"
                      disabled={continuousRun}
                      title={
                        continuousRun
                          ? 'Continuous Run: if schedule end is in December, API end is 31 Dec of the next year; otherwise 31 Dec of the From year.'
                          : undefined
                      }
                      value={toInputDate(
                        processData.toDate ||
                          processData.ToDate ||
                          processData.endDate ||
                          processData.EndDate ||
                          processData.till,
                      )}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="mb-1">
                    <label className="fw-bold">End Time</label>
                    <input
                      type="time"
                      className="form-control"
                      name="endTime"
                      disabled={continuousRun}
                      title={
                        continuousRun
                          ? 'Continuous Run: end time follows year-end (23:59) for the API.'
                          : undefined
                      }
                      value={processData.toDate ? (processData.toDate.split('T')[1] || '').slice(0, 5) : ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">OPCode</label>
                    <input
                      type="text"
                      className="form-control"
                      name="opCode"
                      value={processData.opCode || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Mold Cavity Count</label>
                    <input
                      type="number"
                      className="form-control"
                      name="moldCavityCount"
                      value={processData.moldCavityCount || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Cycle Time</label>
                    <input
                      type="text"
                      className="form-control"
                      name="cycleTime"
                      value={processData.cycleTime || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Work Ticket Number</label>
                    <input
                      type="text"
                      className="form-control"
                      name="workTicketNumber"
                      value={processData.workTicketNumber || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Origin Address</label>
                    <input
                      type="text"
                      className="form-control"
                      name="originAddress"
                      value={processData.originAddress || ''}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold mb-1">Processing Parameter</label>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        const workTicketNumber =
                          processData?.workTicketNumber ||
                          processData?.workTicketNo ||
                          processData?.workTicket;
                        if (!workTicketNumber) {
                          console.warn(
                            'No work ticket number available for Processing Parameter download.',
                            processData,
                          );
                          return;
                        }

                        dispatch(openProcessingParameters(workTicketNumber));
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '36px',
                        height: '36px',
                        padding: '0',
                      }}
                    >
                      <i className="fas fa-download"></i>
                    </button>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-1">
                    <label className="fw-bold">Status</label>
                    <div className="d-flex align-items-center">
                      <div
                        className="me-2 rounded"
                        style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor:
                            STATUS_COLORS[processData.status] || STATUS_COLORS.Default,
                          border: '1px solid #ccc',
                        }}
                        title={`Status color: ${STATUS_COLORS[processData.status] || STATUS_COLORS.Default}`}
                      ></div>
                      <select
                        className="form-control"
                        name="status"
                        value={processData.status || ''}
                        onChange={handleChange}
                        disabled={processStatusLoading}
                      >
                        {processStatusLoading ? (
                          <option>Loading statuses...</option>
                        ) : (
                          statusOptions.map((status, index) => (
                            <option key={index} value={status}>
                              {status}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  {processData.status === 'In Planning' && (
                    <div className="mb-1">
                      <label className="fw-bold">Priority</label>
                      <select
                        className="form-control"
                        name="priority"
                        value={processData.priority || ''}
                        onChange={handleChange}
                      >
                        {priorityOptions.map((priority, index) => (
                          <option key={index} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
                {processData?.processParts?.map((part, index) => (
                  <fieldset key={index} className="border p-2 m-3">
                    <legend className="w-auto">Part {index + 1}</legend>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Part Number</label>
                          <input
                            type="text"
                            className="form-control"
                            name={`partNumber-${index}`}
                            value={part.partNumber || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Box Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`boxQuantity-${index}`}
                            value={part.boxQuantity || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Boxes per Pallet</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`palletQuantity-${index}`}
                            value={part.palletQuantity || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Planned Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            name={`plannedQuantity-${index}`}
                            value={part.plannedQuantity || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">Location</label>
                          <select
                            className="form-control"
                            name={`locationId-${index}`}
                            value={part.locationId || ''}
                            onChange={handleChange}
                          >
                            <option key="0" value="0">
                              Select a Location
                            </option>
                            {locations.map(location => (
                              <option key={location.id} value={location.id}>
                                {location.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">License Plate Number</label>
                          <input
                            type="text"
                            className="form-control"
                            name={`licensePlateNumber-${index}`}
                            value={part.licensePlateNumber || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-1">
                          <label className="fw-bold">PO</label>
                          <input
                            type="text"
                            className="form-control"
                            name={`po-${index}`}
                            value={part.po || ''}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  </fieldset>
                ))}
              </div>
            </div>
          </div>
        </div>

        {activeTab === 'log' && (
          <div className="p-3">
            <div className="alert alert-secondary mb-2">Log</div>
            {statusLogLoading ? (
              <div className="text-center p-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading log entries...</p>
              </div>
            ) : statusLog && Array.isArray(statusLog) && statusLog.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusLog.map((log, index) => {
                      // Start date: use the row's own startTime from the API
                      const startDate = log.startTime ? new Date(log.startTime) : null;
                      
                      // End date: use the previous row's startTime (if it exists)
                      // Since table is in descending order (newest first), previous row (index - 1) is the newer row
                      // If there's no previous row (first row in table), end date should be null (will show "-")
                      let endDate = null;
                      if (index > 0) {
                        // There's a previous row (newer row above), use its startTime as this row's end date
                        const previousLog = statusLog[index - 1];
                        if (previousLog.startTime) {
                          endDate = new Date(previousLog.startTime);
                        }
                      }
                      // If there's no previous row (first/newest row), endDate remains null (will display as "-")

                      // Format date as DD-MM-YYYY HH:MM
                      const formatDateTime = date => {
                        if (!date) return '-';
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${day}-${month}-${year} ${hours}:${minutes}`;
                      };

                      // Get status color for the badge
                      const statusColor = getStatusColor(log.status);
                      const statusText = log.status || 'N/A';

                      return (
                        <tr key={log.id || index}>
                          <td>{log.changedBy || log.ChangedBy || '-'}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: statusColor,
                                color: '#000',
                                fontWeight: '500',
                              }}
                            >
                              {statusText}
                            </span>
                          </td>
                          <td>{formatDateTime(startDate)}</td>
                          <td>{formatDateTime(endDate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted mb-0">No log entries available.</p>
            )}
          </div>
        )}
      </>
    );
  };

  const valid = () => {
    // Prevent backend "Conversion failed when converting date and/or time..." by enforcing required fields.
    const missing = [];

    // End Date is required; continuous run uses schedule end (December → next year 31 Dec) or From-year 31 Dec.
    let endDate = processData?.toDate || processData?.ToDate || processData?.till || processData?.Till;
    if (processData?.continuousRun) {
      const from = processData.fromDate || nowLocalIsoLike();
      const eoy = getContinuousRunEndIso(from, continuousRunReferenceEndRef.current || null);
      if (eoy) endDate = eoy;
    }
    if (isNilOrEmpty(endDate)) missing.push('End Date');

    // Validate parts quantities (must not be null/empty)
    const parts = Array.isArray(processData?.processParts) ? processData.processParts : [];
    if (parts.length === 0) {
      missing.push('Part details (at least 1 part)');
    } else {
      const missingPlanned = parts.some(p => isNilOrEmpty(p?.plannedQuantity));
      const missingPallet = parts.some(p => isNilOrEmpty(p?.palletQuantity));
      if (missingPlanned) missing.push('Planned Quantity');
      if (missingPallet) missing.push('Boxes per Pallet');
    }

    if (missing.length > 0) {
      dispatch(
        showToast({
          message: `Please fill required fields: ${missing.join(', ')}`,
          type: 'danger',
        }),
      );
      return false;
    }

    return true;
  };

  const footer = () => {
    const onSave = async () => {
      if (!valid()) return;

      try {
        const payload = { ...processData };
        if (payload.continuousRun) {
          const from = payload.fromDate || nowLocalIsoLike();
          const eoy = getContinuousRunEndIso(from, continuousRunReferenceEndRef.current || null);
          if (eoy) payload.toDate = eoy;
        }
        const fromTaskBoardDrop = Boolean(processData?.__fromTaskBoardDrop);
        const removeTaskBoardElementId = processData?.__removeTaskBoardElementId;

        delete payload.materialBatchNumber;
        delete payload.MaterialBatchNumber;
        delete payload.__fromTaskBoardDrop;
        delete payload.__removeTaskBoardElementId;

        // Task board → calendar (incomplete fields on drop): complete placement via saveplan.
        // Calendar bar → Plan modal: persist edits via updateprocess.
        if (fromTaskBoardDrop) {
          const ticket = String(
            payload.title ?? payload.workTicketNumber ?? payload.workTicketNo ?? payload.workTicket ?? '',
          ).replace(/^W+/i, '');
          const sinceVal = String(payload.fromDate ?? payload.since ?? '').replace(' ', 'T');
          const tillVal = String(payload.toDate ?? payload.till ?? '').replace(' ', 'T');
          const channel = String(payload.machineId ?? payload.channelUuid ?? '').trim();
          await dispatch(
            savePlan({
              ...payload,
              title: ticket,
              since: sinceVal,
              till: tillVal,
              channelUuid: channel,
              machineId: channel,
            }),
          ).unwrap();
        } else {
          await dispatch(updateProcess(payload)).unwrap();
        }

        if (fromTaskBoardDrop && removeTaskBoardElementId != null) {
          dispatch(plannerActions.removeElement(String(removeTaskBoardElementId)));
        }

        // After a successful save, immediately refresh planner data and completion status
        const filters = planFilters || {};
        await dispatch(
          fetchPlanner({
            searchText: filters.searchText,
            status: filters.status,
            start: filters.start,
            end: filters.end,
          }),
        ).unwrap();
        await dispatch(fetchProcessCompletionStatus()).unwrap();
      } catch (e) {
        // Swallow errors here; existing error handling/toasts (if any) will take care of messaging
      }
    };

    const onConfirmDelete = async () => {
      // Be robust: some payloads may not include id directly
      const deleteId =
        processData?.id ||
        processData?.workTicketNumber ||
        processData?.workTicketNo ||
        processData?.workTicket ||
        processData?.processIdExt;

      if (!deleteId) {
        console.warn('Delete requested but no valid ID found in processData', processData);
        return;
      }

      setIsDeleting(true);
      // Keep confirmation modal open to show loader

      try {
        await dispatch(deleteByWorkTicketId(deleteId)).unwrap();
        setShowDeleteModal(false);
        handleClose();

        // After delete, refresh planner data and completion status
        const filters = planFilters || {};
        try {
          await dispatch(
            fetchPlanner({
              searchText: filters.searchText,
              status: filters.status,
              start: filters.start,
              end: filters.end,
            }),
          ).unwrap();
          await dispatch(fetchProcessCompletionStatus()).unwrap();
        } catch {
          // Ignore refresh errors here; auto-refresh will eventually sync things
        }
      } catch (error) {
        console.error('Delete failed:', error);
        setIsDeleting(false);

        // Check if error has status 500
        // Error from unwrap() will have payload property
        const errorPayload = error?.payload || error;
        const errorStatus = errorPayload?.status || error?.status || error?.response?.status;

        const blockedDeleteStatuses = [400, 403, 409, 422, 500];
        if (blockedDeleteStatuses.includes(Number(errorStatus))) {
          let errorMessage =
            errorPayload?.message ||
            errorPayload?.errorMessage?.message ||
            error?.message ||
            'This work item has not been deleted';

          if (errorMessage.includes('REFERENCE constraint') || errorMessage.includes('ProcessStatusHistory')) {
            errorMessage =
              'This work item cannot be deleted because it has status history records. Please contact your administrator if you need to delete this item.';
          }

          setDeleteError(errorMessage);
          setShowDeleteModal(false);
          setShowErrorModal(true);
        } else {
          // Keep modal open on other errors so user can retry
        }
      }
    };

    // Only show buttons when "Work Ticket Details" tab is active
    if (activeTab !== 'details') {
      return null;
    }

    return (
      <div className="row">
        <div className="col-lg-8">
          <button
            className="btn btn-danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Deleting...
              </>
            ) : (
              'Delete Work Item'
            )}
          </button>

          <ConfirmModal
            showModal={showDeleteModal}
            title="Delete Work Item"
            message="Are you sure you want to delete this work item?"
            onCloseModal={() => {
              if (!isDeleting) {
                setShowDeleteModal(false);
              }
            }}
            onConfirmModal={onConfirmDelete}
            isLoading={isDeleting}
            confirmText="Confirm Delete"
          />

          {/* Error Modal for 500 status */}
          <Modal
            id="delete-error-modal"
            show={showErrorModal}
            size="sm"
            title="Delete Failed"
            body={() => (
              <div className="row">
                <div className="col-12 text-center">
                  <p className="mb-0" style={{ color: '#666', fontSize: '14px' }}>
                    {deleteError || 'This work item has not been deleted'}
                  </p>
                </div>
              </div>
            )}
            footer={() => (
              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowErrorModal(false);
                    handleClose();
                  }}
                  style={{ minWidth: '80px' }}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowErrorModal(false);
                    handleClose();
                  }}
                  style={{ minWidth: '80px' }}
                >
                  OK
                </button>
              </div>
            )}
            handleClose={() => {
              setShowErrorModal(false);
              handleClose();
            }}
          />
        </div>
        <div className="col-lg-4">
          <FooterButton
            cancelText="Cancel"
            saveText="Save Plan"
            onSave={onSave}
            onCancel={handleClose}
            disableSave={!isFormDirty}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="program-modal"
      show={isShowModal}
      size="xl"
      title="Plan"
      body={body}
      footer={footer}
      handleClose={handleClose}
    />
  );
};

ProgramAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.shape(CustomPropTypes.Shipment),
  onCloseModal: PropTypes.func,
};

export default AppModal(ProgramAddEdit);
